import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '@bill-alps/backend/convex/_generated/api';
import type { Id } from '@bill-alps/backend/convex/_generated/dataModel';
import type { BillingModuleKey } from '@bill-alps/backend/features/companies/modules';
import type { ZefixCompanyPrefill } from '@bill-alps/backend/features/zefix/models';
import { Building2, Loader2, MailCheck } from 'lucide-react';
import {
  Callout,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  Badge,
  Button,
  cn,
  Label,
  toast,
} from '@bill-alps/ui';
import { useTranslation } from '../i18n/useTranslation';
import { handleMutationError } from '@/lib/errors/handle-mutation-error';
import { FIDUCIAIRE_ERROR_CODE_MAP } from '@/lib/errors/fiduciaire-errors';
import {
  estimateBillingChange,
  projectModuleQuantities,
} from '@/lib/billing/fiduciaire-billing';
import {
  BillingEstimateSummary,
  FiduciaireModulePicker,
  PICKABLE_MODULES,
} from './fiduciaire-module-picker';
import { FiduciaireTierTable } from './fiduciaire-tier-table';
import { ZefixCompanySearch } from './zefix-company-search';
import { ZefixPrefillSummary } from './zefix-prefill-summary';

interface AddMandateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Payer = 'fiduciaire' | 'client';
const PAYERS: readonly Payer[] = ['fiduciaire', 'client'];

/**
 * Add a mandate. Two outcomes behind one form: a company nobody runs on Bill
 * Alps yet is CREATED — billed per module on the trustee's subscription, or
 * left to the client to subscribe (Starter until then) — while a company
 * already on Bill Alps under another account gets a mandate REQUEST: its
 * administrators are emailed and the trustee waits for their answer. The
 * search widget tells the two apart before submit; who pays a requested
 * company is decided later, from the mandates page.
 */
export function AddMandateDialog({
  open,
  onOpenChange,
}: AddMandateDialogProps) {
  const { t } = useTranslation(['fiduciaire', 'zefix']);
  const navigate = useNavigate();
  const fiduciaireBilling = useQuery(
    api.features.fiduciaire.mandates.getMyFiduciaire,
    open ? {} : 'skip',
  );
  const createManagedCompany = useMutation(
    api.features.fiduciaire.mandates.createManagedCompany,
  );
  const createCheckoutSession = useAction(
    api.features.fiduciaire.billing.createFiduciaireCheckoutSession,
  );

  const [step, setStep] = useState<'form' | 'paywall'>('form');
  const [name, setName] = useState('');
  const [prefill, setPrefill] = useState<ZefixCompanyPrefill | null>(null);
  const [payer, setPayer] = useState<Payer>('fiduciaire');
  const [modules, setModules] = useState<BillingModuleKey[]>([
    ...PICKABLE_MODULES,
  ]);
  const [loading, setLoading] = useState(false);

  const existing = prefill?.existingCompany ?? null;
  const fiduciairePays = payer === 'fiduciaire';
  const quantities = fiduciaireBilling?.moduleQuantities ?? {};
  const estimate = estimateBillingChange(quantities, modules);

  const close = (nextOpen: boolean) => {
    if (!nextOpen) {
      setStep('form');
      setName('');
      setPrefill(null);
      setPayer('fiduciaire');
      setModules([...PICKABLE_MODULES]);
      setLoading(false);
    }
    onOpenChange(nextOpen);
  };

  const canSubmit =
    !!name.trim() &&
    !existing?.owned &&
    (!!existing || !fiduciairePays || modules.length > 0);

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!canSubmit) return;
    setLoading(true);
    try {
      const result = existing
        ? await createManagedCompany({
            companyId: existing.companyId as Id<'companies'>,
          })
        : await createManagedCompany({
            name: trimmed,
            payer,
            ...(fiduciairePays ? { modules } : {}),
            ...(prefill?.ide ? { ide: prefill.ide } : {}),
            ...(prefill?.canton ? { canton: prefill.canton } : {}),
            ...(prefill?.address ? { address: prefill.address } : {}),
          });
      if (result.status === 'subscription_required') {
        setStep('paywall');
      } else if (result.status === 'pending') {
        toast.success(
          t('addMandate.requestSent', { name: result.companyName }),
        );
        close(false);
        navigate('/mandates');
      } else {
        toast.success(t('addMandate.created', { name: trimmed }));
        close(false);
        navigate('/dashboard');
      }
    } catch (err) {
      handleMutationError(
        err,
        t,
        FIDUCIAIRE_ERROR_CODE_MAP,
        'fiduciaire:errors.generic',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSetUpBilling = async () => {
    setLoading(true);
    try {
      // The webhook creates the mandate with exactly the modules picked here.
      const { url } = await createCheckoutSession({
        pendingMandate: { name: name.trim(), modules },
      });
      if (url) {
        window.location.href = url;
      } else {
        setLoading(false);
        toast.error(t('errors.generic'));
      }
    } catch (err) {
      setLoading(false);
      handleMutationError(
        err,
        t,
        FIDUCIAIRE_ERROR_CODE_MAP,
        'fiduciaire:errors.generic',
      );
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={close}>
      <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
        {step === 'form' ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('addMandate.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('addMandate.description')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void handleCreate();
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="mandate-name">
                  {t('addMandate.nameLabel')}
                </Label>
                <ZefixCompanySearch
                  id="mandate-name"
                  value={name}
                  onValueChange={setName}
                  onSelect={setPrefill}
                  placeholder={t('addMandate.namePlaceholder')}
                  autoFocus
                  detectExisting
                  disablePortal
                  inlineResults
                />
                {existing ? (
                  <div
                    data-testid="mandate-existing-summary"
                    className="rounded-xl border-2 border-border bg-muted/40 p-3 text-sm"
                  >
                    <div className="flex items-center gap-2 font-medium">
                      <Building2 className="size-4 shrink-0 text-muted-foreground" />
                      {t('addMandate.existingTitle')}
                      <Badge
                        size="sm"
                        variant={existing.owned ? 'outline-teal' : 'blue'}
                      >
                        {existing.owned
                          ? t('zefix:search.ownedBadge')
                          : t('zefix:search.existingBadge')}
                      </Badge>
                    </div>
                    <p className="mt-2 text-muted-foreground">
                      {existing.owned
                        ? t('addMandate.existingOwned')
                        : t('addMandate.existingSummary', {
                            name: existing.name,
                          })}
                    </p>
                    {prefill?.ide && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t('zefix:prefill.ide')} : {prefill.ide}
                      </p>
                    )}
                  </div>
                ) : (
                  prefill && (
                    <ZefixPrefillSummary
                      prefill={prefill}
                      onClear={() => setPrefill(null)}
                    />
                  )
                )}
              </div>

              {/* Who pays and for what: a NEW company only. A requested
                  company keeps its own billing until taken over later. */}
              {!existing && (
                <>
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">
                      {t('addMandate.payerLabel')}
                    </legend>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {PAYERS.map((option) => (
                        <label
                          key={option}
                          className={cn(
                            'flex cursor-pointer items-start gap-3 rounded-md border-2 border-border p-3 transition-colors hover:bg-accent/30',
                            payer === option && 'border-primary bg-primary/5',
                          )}
                        >
                          <input
                            type="radio"
                            name="mandate-payer"
                            value={option}
                            checked={payer === option}
                            onChange={() => setPayer(option)}
                            disabled={loading}
                            className="mt-1 accent-primary"
                            data-testid={`mandate-payer-${option}`}
                          />
                          <span className="flex-1">
                            <span className="block text-sm font-medium">
                              {t(`addMandate.payer.${option}`)}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                              {t(`addMandate.payerHint.${option}`)}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  {fiduciairePays ? (
                    <div className="space-y-2">
                      <Label>{t('addMandate.modulesLabel')}</Label>
                      <FiduciaireModulePicker
                        idPrefix="add-mandate"
                        value={modules}
                        onChange={setModules}
                        disabled={loading}
                      />
                      {modules.length === 0 ? (
                        <p className="text-xs text-riso-pink">
                          {t('addMandate.noModules')}
                        </p>
                      ) : (
                        <BillingEstimateSummary estimate={estimate} />
                      )}
                    </div>
                  ) : (
                    <Callout variant="info" size="sm">
                      {t('addMandate.clientPaysNote')}
                    </Callout>
                  )}
                </>
              )}
            </form>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={loading}>
                {t('addMandate.cancel')}
              </AlertDialogCancel>
              <Button
                type="button"
                onClick={() => void handleCreate()}
                disabled={loading || !canSubmit}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : existing ? (
                  <MailCheck className="h-4 w-4" />
                ) : null}
                {existing
                  ? t('addMandate.requestSubmit')
                  : t('addMandate.submit')}
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('paywall.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('paywall.description', { name: name.trim() })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-3">
              <BillingEstimateSummary estimate={estimate} />
              <FiduciaireTierTable
                moduleQuantities={projectModuleQuantities(quantities, modules)}
                showOverage={false}
              />
              <p className="text-xs text-muted-foreground">
                {t('paywall.checkoutNote')}
              </p>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={loading}>
                {t('addMandate.cancel')}
              </AlertDialogCancel>
              <Button
                type="button"
                onClick={() => void handleSetUpBilling()}
                disabled={loading}
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('paywall.setUpBilling')}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
